import { NavLink } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, Clock, IdCard, LogOut, X } from 'lucide-react';
import { MedicusLogo } from './MedicusLogo';
import { useAuthStore } from '@lib/store/auth.store';
import { esTelemarketer, isMedicus, ROL_LABELS, UserRole } from '@lib/types';
import { puedeVer } from '@lib/permissions/catalog';
import { ITEMS } from './sidebar.items';
import { usePendientes } from '@lib/hooks/usePendientes';

const esTmk = (r: UserRole) => esTelemarketer(r);

interface Props {
  /** `open`/`onClose` controlan el drawer en mobile; en desktop el sidebar es
   *  fijo y estas props no tienen efecto (no hay botón hamburguesa). */
  open?: boolean;
  onClose?: () => void;
  /** Desktop: solo íconos, con el nombre de la sección como tooltip. */
  colapsado?: boolean;
  onToggleColapsado?: () => void;
}

export function Sidebar({ open = false, onClose, colapsado = false, onToggleColapsado }: Props) {
  const { user, logout } = useAuthStore();
  // Antes del early return: los hooks tienen que correr en el mismo orden en
  // cada render. `usePendientes` acepta `null` y en ese caso no consulta nada.
  const pendientes = usePendientes(user);
  if (!user) return null;

  const visible = ITEMS.filter((it) => puedeVer(user, it.permiso));
  const veActividad = puedeVer(user, 'view:actividad');
  const clases = ['sidebar', open && 'sidebar--open', colapsado && 'sidebar--colapsada']
    .filter(Boolean)
    .join(' ');
  // Colapsado, el texto no se ve: el nombre va en `title` para que el hover lo
  // muestre. Abierto no hace falta, y un tooltip igual al texto es ruido.
  const tooltip = (label: string) => (colapsado ? label : undefined);

  return (
    <aside className={clases}>
      <div className="sidebar__brand">
        {!colapsado && <MedicusLogo width={108} />}
        {onToggleColapsado && (
          <button
            type="button"
            className="sidebar__toggle"
            aria-label={colapsado ? 'Expandir menú' : 'Colapsar menú'}
            title={colapsado ? 'Expandir menú' : 'Colapsar menú'}
            aria-expanded={!colapsado}
            onClick={onToggleColapsado}
          >
            {colapsado ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          </button>
        )}
        <button
          type="button"
          className="sidebar__close"
          aria-label="Cerrar menú"
          onClick={() => onClose?.()}
        >
          <X size={20} />
        </button>
      </div>
      {!colapsado && <p className="sidebar__product">Carga Masiva</p>}

      <nav className="sidebar__nav">
        {visible.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              title={tooltip(it.label)}
              onClick={() => onClose?.()}
              className={({ isActive }) =>
                isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link'
              }
            >
              <Icon size={18} strokeWidth={2} style={{ opacity: 0.9 }} />
              <span className="sidebar__linkText">{it.label}</span>
              <BadgePendientes cantidad={pendientes[it.to]} seccion={it.label} />
            </NavLink>
          );
        })}

        {/* Admin / Super Admin: "Actividad" debajo del nav principal */}
        {isMedicus(user.rol) && veActividad && (
          <NavLink
            to="/actividad"
            title={tooltip('Actividad')}
            onClick={() => onClose?.()}
            className={({ isActive }) =>
              isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link'
            }
            style={{ marginTop: '0.5rem' }}
          >
            <Clock size={18} strokeWidth={2} style={{ opacity: 0.9 }} />
            <span className="sidebar__linkText">Actividad</span>
          </NavLink>
        )}
      </nav>

      {colapsado ? (
        // Colapsado: la card del usuario no entra; quedan el perfil y salir,
        // como íconos con tooltip.
        <div className="sidebar__footer">
          <NavLink
            to="/perfil"
            className="sidebar__link"
            title={`Mi perfil · ${user.nombre}`}
            aria-label="Mi perfil"
          >
            <IdCard size={18} strokeWidth={2} />
          </NavLink>
          <button
            type="button"
            className="sidebar__link sidebar__iconButton"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
            onClick={logout}
          >
            <LogOut size={18} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <div className="sidebar__footer">
          <div className="sidebar__user">
            <p className="sidebar__userName">{user.nombre}</p>
            <p className="sidebar__userMeta">{ROL_LABELS[user.rol]}</p>
            <p className="sidebar__userEmail">{user.email}</p>

            {/* El perfil propio no depende de permisos: lo ve cualquiera. */}
            <NavLink
              to="/perfil"
              onClick={() => onClose?.()}
              className="sidebar__userActivity"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 8,
                fontSize: 12,
                color: 'var(--color-primary-darker)',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              <IdCard size={14} strokeWidth={2} />
              <span>Mi perfil</span>
            </NavLink>

            {/* Usuario Empresa: "Actividad" dentro de la card del usuario.
                TMK no la ve — su scope es solo el buscador. */}
            {!isMedicus(user.rol) && !esTmk(user.rol) && veActividad && (
              <NavLink
                to="/actividad"
                className="sidebar__userActivity"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 8,
                  fontSize: 12,
                  color: 'var(--color-primary-darker)',
                  textDecoration: 'none',
                  fontWeight: 600,
                }}
              >
                <Clock size={14} strokeWidth={2} />
                <span>Actividad</span>
              </NavLink>
            )}
          </div>
          <button className="btn btn-ghost sidebar__logout" onClick={logout}>
            Cerrar sesión
          </button>
        </div>
      )}
    </aside>
  );
}

/**
 * Contador de pendientes al lado del nombre de la sección.
 *
 * No se dibuja si no hay dato o si el pendiente es cero: un círculo rojo con un
 * 0 llama la atención para decir que no pasa nada. Y `undefined` (la consulta
 * falló) tampoco muestra 0, porque sería afirmar algo que no sabemos.
 *
 * A partir de 100 muestra "99+": el ancho del menú es fijo y un número de cuatro
 * cifras empuja el texto de la sección. Con el menú colapsado se ve como un
 * punto sobre el ícono (ver `.sidebar--colapsada .sidebar__badge`).
 */
function BadgePendientes({ cantidad, seccion }: { cantidad?: number; seccion: string }) {
  if (!cantidad || cantidad <= 0) return null;
  const texto = cantidad > 99 ? '99+' : String(cantidad);
  return (
    <span
      className="sidebar__badge"
      title={`${cantidad} pendiente${cantidad === 1 ? '' : 's'} en ${seccion}`}
      aria-label={`${cantidad} pendiente${cantidad === 1 ? '' : 's'}`}
    >
      {texto}
    </span>
  );
}
